/* The shared shell for the two CRM tables — mockups/crm-v3-contacts-v1.html
   sections #a and #clients. Contacts and Clients draw the same furniture (the
   filter row, the bordered table, the footer, the drawer beside it) and only
   the columns differ, so the furniture lives here and each view supplies its
   own cells.

   Every colour is a token from src/lib/styles.js. Where the mockup uses a
   lighter step than --muted for a hint or a mono label, that is C.muted at
   reduced opacity rather than a new hex. */
import { C, font, mono, sel } from "../../lib/styles.js";
import { CONSENT_COLORS } from "../../lib/constants.js";

const CC = CONSENT_COLORS(C);

/** The consent dot, wherever consent is drawn — the two lists, both drawers,
 *  the Segments stack and its sample. `no_bulk` is hollow: the absence of a
 *  colour is the point, because it is the absence of permission. */
export const consentDotStyle = (tone) => (tone === "no_bulk"
  ? { ...S.consentDot, background: "transparent", border: `1px solid ${C.muted}` }
  : { ...S.consentDot, background: tone === "unsubscribed" ? C.danger : CC[tone] || C.muted });

/** The colour a consent state is drawn in — the stacked bar's segments. */
export const consentColor = (tone) =>
  (tone === "unsubscribed" ? C.danger : tone === "no_bulk" ? C.border : CC[tone] || C.muted);

/* The mockup's row tint. C.surfaceAlt at 50%, so hovering never introduces a
   colour the token set does not already have. */
export const HOVER_TINT = `${C.surfaceAlt}80`;

export const S = {
  /* ── filter row ── */
  filters: {
    display: "flex", alignItems: "center", gap: 6, margin: "0 0 10px",
    flexWrap: "wrap", fontFamily: font,
  },
  gap: { width: 10 },
  selWrap: {
    display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 4px 3px 10px",
    borderRadius: 5, fontSize: 11, color: C.muted, border: `1px solid ${C.border}`,
    background: C.surface,
  },
  selLabel: { whiteSpace: "nowrap" },
  select: {
    ...sel, width: "auto", padding: "2px 22px 2px 4px", fontSize: 11, background: "transparent",
    backgroundImage: sel.backgroundImage, backgroundRepeat: "no-repeat",
    backgroundPosition: "right 6px center", border: "none", color: C.text,
    borderRadius: 4, cursor: "pointer",
  },
  count: { marginLeft: "auto", fontSize: 11, color: C.muted, whiteSpace: "nowrap" },

  /* ── the bordered table box ── */
  box: {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
    overflow: "hidden", fontFamily: font,
  },
  /* The table scrolls sideways inside its own box; the page never does. */
  scroller: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    fontFamily: mono, fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase",
    color: C.muted, opacity: 0.8, fontWeight: 500, textAlign: "left", padding: "9px 12px",
    background: C.surfaceAlt, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
  },
  thRight: { textAlign: "right" },
  td: {
    padding: "9px 12px", borderBottom: `1px solid ${C.border}`, fontSize: 13,
    verticalAlign: "middle", whiteSpace: "nowrap",
  },
  tdFirst: { whiteSpace: "normal", minWidth: 200 },
  tdRight: { textAlign: "right" },
  num: { fontVariantNumeric: "tabular-nums" },
  mono: { fontFamily: mono, fontSize: 12, fontVariantNumeric: "tabular-nums" },

  /* ── row furniture ── */
  who: { lineHeight: 1.3 },
  whoName: { fontSize: 13, fontWeight: 400 },
  whoMeta: { fontSize: 11.5, color: C.muted, lineHeight: 1.35 },
  dot: { opacity: 0.5, margin: "0 4px" },
  tag: {
    display: "inline-block", fontSize: 10.5, color: C.muted, background: C.surfaceAlt,
    padding: "1px 6px", borderRadius: 3, marginRight: 3,
  },
  codePill: {
    fontFamily: mono, fontSize: 10.5, color: C.muted, background: C.surfaceAlt,
    padding: "1px 5px", borderRadius: 3, marginLeft: 6,
  },
  indPill: {
    fontSize: 9.5, color: C.teal, padding: "1px 5px", background: `${C.teal}18`, borderRadius: 3,
  },
  role: {
    display: "inline-block", padding: "2px 7px", borderRadius: 4, fontSize: 9.5,
    fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em", marginRight: 4,
  },
  consent: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: C.muted },
  consentDot: { width: 7, height: 7, borderRadius: "50%", display: "inline-block", flexShrink: 0 },
  touch: { fontSize: 12, color: C.muted },
  touchNow: { color: C.text, fontWeight: 400 },
  touchChannel: { fontFamily: mono, fontSize: 10, color: C.muted, opacity: 0.75, marginLeft: 6 },
  linkedin: { fontFamily: mono, fontSize: 10.5, color: C.indigo },
  linkedinOff: { fontFamily: mono, fontSize: 10.5, color: C.muted, opacity: 0.75 },
  bar: {
    display: "inline-block", height: 6, borderRadius: 2, background: C.surfaceAlt,
    verticalAlign: "middle", width: 70, marginRight: 8, position: "relative", overflow: "hidden",
  },
  barFill: { position: "absolute", left: 0, top: 0, bottom: 0, background: C.stone, borderRadius: 2 },
  openItem: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: C.danger },
  openDot: { width: 6, height: 6, borderRadius: "50%", background: C.danger, display: "inline-block" },
  small: { color: C.muted, opacity: 0.8, fontSize: 11, marginLeft: 4 },

  /* ── footer ── */
  foot: {
    display: "flex", alignItems: "center", gap: 14, padding: "9px 12px", fontSize: 11.5,
    color: C.muted, background: C.surface, borderTop: `1px solid ${C.border}`, flexWrap: "wrap",
  },
  pager: { marginLeft: "auto", display: "flex", gap: 4 },
  page: {
    padding: "2px 7px", borderRadius: 4, border: `1px solid ${C.border}`, fontFamily: mono,
    fontSize: 10.5, background: "transparent", color: C.muted, cursor: "pointer",
  },
  pageOn: { background: C.text, color: "#FFF", borderColor: "transparent" },
  pageGap: { padding: "2px 4px", fontFamily: mono, fontSize: 10.5, color: C.muted, opacity: 0.7 },
  empty: { textAlign: "center", padding: 40, color: C.muted, fontSize: 13 },

  /* ── drawer ── */
  drawer: {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
    overflow: "hidden", position: "sticky", top: 8, fontFamily: font,
  },
  drawerMobile: {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
    overflow: "hidden", fontFamily: font,
  },
  dhead: { padding: "14px 16px 12px", borderBottom: `1px solid ${C.border}` },
  dback: {
    fontFamily: mono, fontSize: 10.5, color: C.muted, opacity: 0.85, marginBottom: 8,
    display: "flex", justifyContent: "space-between", alignItems: "center",
  },
  dbackBtn: {
    fontFamily: mono, fontSize: 10.5, color: C.muted, background: "transparent",
    border: "none", padding: 0, cursor: "pointer",
  },
  dname: { fontSize: 17, fontWeight: 400, lineHeight: 1.2 },
  dco: { fontSize: 12.5, color: C.muted, marginTop: 2 },
  dcoLink: {
    color: C.text, background: "transparent", border: "none", padding: 0, cursor: "pointer",
    borderBottom: `1px solid ${C.border}`, fontSize: 12.5, fontFamily: font,
  },
  dchips: { display: "flex", gap: 5, marginTop: 8, flexWrap: "wrap", alignItems: "center" },
  dsec: { padding: "12px 16px", borderBottom: `1px solid ${C.border}` },
  dsecLast: { padding: "12px 16px" },
  dsecTitle: {
    fontFamily: mono, fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase",
    color: C.muted, opacity: 0.85, fontWeight: 500, marginBottom: 8,
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
  },
  dsecAction: {
    color: C.muted, fontWeight: 400, letterSpacing: 0, textTransform: "none",
    fontFamily: font, fontSize: 11, background: "transparent", border: "none",
    padding: 0, cursor: "pointer",
  },
  kv: { display: "grid", gridTemplateColumns: "82px minmax(0,1fr)", gap: "5px 10px", fontSize: 12.5 },
  kvK: { color: C.muted, opacity: 0.85 },
  kvV: { color: C.text, minWidth: 0, overflowWrap: "anywhere" },
  kvVMono: { fontFamily: mono, fontSize: 11.5 },
  tiles: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 8 },
  tile: { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 10px" },
  tileN: { fontFamily: mono, fontSize: 14, fontVariantNumeric: "tabular-nums" },
  tileL: { fontSize: 10.5, color: C.muted, marginTop: 2 },
  danger: {
    color: C.danger, background: "transparent", border: "none", padding: 0,
    cursor: "pointer", fontSize: 11, fontFamily: font,
  },
  moreBtn: {
    marginTop: 10, fontFamily: mono, fontSize: 10, letterSpacing: "0.08em",
    textTransform: "uppercase", color: C.muted, background: "transparent",
    border: `1px solid ${C.border}`, borderRadius: 4, padding: "3px 8px", cursor: "pointer",
  },
};

/** The two-column layout from variant A. The drawer sits beside the table, not
 *  over it, so the row you clicked keeps its place. On a phone there is no
 *  room for both, and the drawer takes the screen. */
export const layout = (drawerOpen, isMobile) => ({
  display: "grid",
  gridTemplateColumns: drawerOpen && !isMobile ? "minmax(0, 1fr) 380px" : "minmax(0, 1fr)",
  gap: 14,
  alignItems: "start",
});

/* The hover tint is CSS, not state.

   It used to be a hoverId in each view, so moving the mouse across a 40-row
   table re-rendered the whole table on every row boundary — for a colour the
   browser can apply itself. The rule is scoped to the row class and only
   reaches cells that carry no inline background, which is every unselected
   cell: a selected row keeps its inline background and inline wins, so
   selection still beats hover without an !important anywhere.

   ROW_CLASS goes on the <tr>; ROW_HOVER_CSS is rendered once per table by
   <ListRowStyle/> in ListParts.jsx, built from the token rather than a hex
   copied into responsive.css. */
export const ROW_CLASS = "crm-list-row";
export const ROW_HOVER_CSS = `.${ROW_CLASS}:hover > td { background: ${HOVER_TINT}; }`;

export const cellStyle = (base, selected, first) => ({
  ...S.td,
  ...(first ? S.tdFirst : null),
  ...base,
  // Only a selected cell paints itself; the rest are left for the CSS above.
  ...(selected ? { background: C.bg } : null),
  ...(selected && first ? { boxShadow: `inset 3px 0 0 ${C.text}` } : null),
});
